import React, { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Menu,
  MenuItem,
  styled,
  Backdrop,
  Tooltip
} from "@mui/material"
import { Formik } from "formik"
import { useDispatch, useSelector } from "react-redux"
import { useTheme } from "@mui/system"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import BarChartIcon from "@mui/icons-material/BarChart"
import AppButton from "components/StyledComponents/AppButton"
import InfoTooltip from "components/InfoTooltip"
import TableNoRecords from "components/TableNoRecords"
import DeleteConfirmationDialog from "components/DeleteConfirmationDialog"
import AppPagination from "components/StyledComponents/AppPagination"
import ShoppingSpreeForm from "./components/ShoppingSpreeForm"
import SpreeDataModal from "./components/SpreeDataModal"
import {
  getShoppingSpreeWindowsAction,
  createShoppingSpreeWindowAction,
  updateShoppingSpreeWindowAction,
  deleteShoppingSpreeWindowAction,
  getSpreeWindowQuantitiesAction
} from "redux/superAdmin/actions"
import { superAdminAction } from "redux/superAdmin/slice/user"
import { validationSchemaShoppingSpree } from "helpers/validationSchema"
import { formatDate, toTitleCase } from "helpers/functions"
import { shoppingSpreeColumns } from "helpers/constant"
import moment from "moment"

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  padding: "12px 24px",
  "&:hover": { backgroundColor: theme.palette.action.hover }
}))

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "16px 24px 16px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": { paddingRight: 0 }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid #272a2c`,
    "&:last-child": { borderBottom: "none" }
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "sticky",
    borderBottom: `2px solid #272a2c`,
    "&:last-child": { paddingRight: 0 }
  }
}))

const ActionsMenu = ({ onEdit, onDelete, status }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const openMenu = Boolean(anchorEl)
  const theme = useTheme()

  const handleMenuClose = () => setAnchorEl(null)

  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ color: "text.secondary" }}
        // in_progress has no actions (no edit, no delete); completed is locked too
        disabled={status === "completed" || status === "in_progress"}>
        <MoreHorizIcon fontSize="small" />
      </IconButton>
      <Backdrop
        sx={{ color: theme.palette.white.main, zIndex: 1300 }}
        open={openMenu}
        onClick={handleMenuClose}>
        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              marginTop: "15px",
              backgroundColor: theme.palette.background.default,
              borderRadius: "12px",
              "& .MuiList-root": {
                backgroundColor: theme.palette.background.default,
                width: "150px",
                boxSizing: "border-box",
                padding: "10px 0"
              }
            }
          }}>
          {status !== "in_progress" && (
            <StyledMenuItem
              onClick={() => {
                handleMenuClose()
                onEdit()
              }}>
              <Typography variant="body1Regular">Edit</Typography>
            </StyledMenuItem>
          )}
          {status !== "in_progress" && (
            <StyledMenuItem
              onClick={() => {
                handleMenuClose()
                onDelete()
              }}>
              <Typography sx={{ color: theme.palette.red.main }} variant="body1Regular">
                Delete
              </Typography>
            </StyledMenuItem>
          )}
        </Menu>
      </Backdrop>
    </>
  )
}

const ITEMS_PER_PAGE = 10

const ShoppingSpreeConfiguration = () => {
  const dispatch = useDispatch()
  const { shoppingSpreeWindows } = useSelector((state) => state.superAdmin)
  const { isLoading } = useSelector((state) => state.app)
  const theme = useTheme()
  const [formOpen, setFormOpen] = useState(false)
  const [selectedWindow, setSelectedWindow] = useState(null)
  const [isEdit, setIsEdit] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [windowToDelete, setWindowToDelete] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [dataModalOpen, setDataModalOpen] = useState(false)

  const handleViewData = (win) => {
    dispatch(superAdminAction.setSpreeWindowData(null))
    dispatch(getSpreeWindowQuantitiesAction({ windowId: win.id }))
    setDataModalOpen(true)
  }

  useEffect(() => {
    dispatch(getShoppingSpreeWindowsAction())
  }, [])

  const totalPages = Math.ceil((shoppingSpreeWindows?.length || 0) / ITEMS_PER_PAGE)
  const paginatedWindows = shoppingSpreeWindows?.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  const initialValues = {
    max_rewards_per_user: "",
    shopping_spree_start_time: "",
    shopping_spree_start_date: null,
    shopping_spree_end_time: "",
    shopping_spree_end_date: null
  }

  const initialValuesEdit = {
    max_rewards_per_user: selectedWindow?.max_rewards_per_user || "",
    shopping_spree_start_time: selectedWindow?.spree_start_time
      ? selectedWindow.spree_start_time.slice(0, 5)
      : "",
    shopping_spree_start_date: selectedWindow?.spree_start_date
      ? new Date(selectedWindow.spree_start_date)
      : null,
    shopping_spree_end_time: selectedWindow?.spree_end_time
      ? selectedWindow.spree_end_time.slice(0, 5)
      : "",
    shopping_spree_end_date: selectedWindow?.spree_end_date
      ? new Date(selectedWindow.spree_end_date)
      : null
  }

  const handleFormSave = (formData) => {
    const payload = {
      max_rewards_per_user: formData.max_rewards_per_user,
      spree_start_date: formData.shopping_spree_start_date
        ? formatDate(formData.shopping_spree_start_date)
        : null,
      spree_start_time: formData.shopping_spree_start_time
        ? `${formData.shopping_spree_start_time}:00`
        : null,
      spree_end_date: formData.shopping_spree_end_date
        ? formatDate(formData.shopping_spree_end_date)
        : null,
      spree_end_time: formData.shopping_spree_end_time
        ? `${formData.shopping_spree_end_time}:00`
        : null
    }

    if (selectedWindow?.id) {
      dispatch(
        updateShoppingSpreeWindowAction({
          windowId: selectedWindow.id,
          payload,
          callback: onSaveSuccess
        })
      )
    } else {
      dispatch(createShoppingSpreeWindowAction({ payload, callback: onSaveSuccess }))
    }
  }

  const onSaveSuccess = () => {
    setFormOpen(false)
    setSelectedWindow(null)
    setIsEdit(false)
  }

  const handleConfirmDelete = () => {
    dispatch(
      deleteShoppingSpreeWindowAction({
        windowId: windowToDelete.id,
        callback: () => {
          setDeleteDialogOpen(false)
          setWindowToDelete(null)
        }
      })
    )
  }
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography variant="body2Regular" color="text.secondary">
          Shopping sprees are time-boxed windows. During a spree, each reward&apos;s quantity is set
          per spree and resets every window.
        </Typography>
        <InfoTooltip
          title={
            "Create one or more spree windows with a start/end date-time (UTC) and a Max Rewards/User cap.\n\n" +
            "• A reward only appears during a spree if it has a Quantity set for that spree (Rewards tab → Quantity Available).\n" +
            "• While a spree is active, a member can redeem up to the spree's Max Rewards/User cap, and each reward up to its own quantity.\n" +
            "• Outside any spree, per-spree quantity limits don't apply."
          }
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
        <AppButton
          onClick={() => {
            setSelectedWindow(null)
            setIsEdit(false)
            setFormOpen(true)
          }}
          sx={{ padding: "10px 24px" }}>
          + Add Shopping Spree Window
        </AppButton>
      </Box>

      <Paper sx={{ mt: 3 }}>
        <TableContainer sx={{ maxHeight: 700 }}>
          <CustomTable stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="body2Regular" style={{ opacity: 1 }}>
                    #
                  </Typography>
                </TableCell>
                {shoppingSpreeColumns.map((col) => (
                  <TableCell key={col.id} align={col.align} sx={{ minWidth: col.minWidth }}>
                    <Typography variant="body2Regular" style={{ opacity: 0.6 }}>
                      {col.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedWindows?.length ? (
                paginatedWindows.map((win, idx) => (
                  <TableRow key={win.id}>
                    <TableCell>
                      <Typography variant="body2Regular">
                        {(currentPage - 1) * 10 + idx + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2Regular">
                        {win.spree_start_date
                          ? moment(win.spree_start_date).format("MMM DD, YYYY")
                          : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2Regular">
                        {win.spree_end_date
                          ? moment(win.spree_end_date).format("MMM DD, YYYY")
                          : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2Regular"
                        sx={{
                          color:
                            win?.status === "in_progress"
                              ? theme.palette.orange.secondary
                              : win?.status === "completed"
                              ? theme.palette.green.main
                              : theme.palette.error.main
                        }}>
                        {toTitleCase(win?.status?.replace("_", " "))}
                      </Typography>
                    </TableCell>
                    <TableCell align={shoppingSpreeColumns[3]?.align}>
                      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                        <Tooltip title="View spree data">
                          <IconButton
                            size="small"
                            sx={{ color: "text.secondary" }}
                            onClick={() => handleViewData(win)}>
                            <BarChartIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <ActionsMenu
                          status={win?.status}
                          onEdit={() => {
                            setSelectedWindow(win)
                            setIsEdit(true)
                            setFormOpen(true)
                          }}
                          onDelete={() => {
                            setWindowToDelete(win)
                            setDeleteDialogOpen(true)
                          }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableNoRecords
                  colSpan={shoppingSpreeColumns.length + 1}
                  label={
                    isLoading
                      ? "Loading shopping spree windows..."
                      : "No shopping spree windows yet. Use 'Add Shopping Spree Window' to create one."
                  }
                />
              )}
            </TableBody>
          </CustomTable>
        </TableContainer>
        <AppPagination
          onPageChange={setCurrentPage}
          totalPages={totalPages}
          currentPage={currentPage}
        />
      </Paper>

      {formOpen && (
        <Formik
          onSubmit={handleFormSave}
          validationSchema={validationSchemaShoppingSpree}
          initialValues={selectedWindow ? initialValuesEdit : initialValues}
          enableReinitialize>
          {(formik) => (
            <ShoppingSpreeForm
              open={formOpen}
              onClose={() => {
                setFormOpen(false)
                setSelectedWindow(null)
                setIsEdit(false)
              }}
              formik={formik}
              isEdit={isEdit}
              loading={isLoading}
            />
          )}
        </Formik>
      )}

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false)
          setWindowToDelete(null)
        }}
        onDelete={handleConfirmDelete}
        title="Delete Shopping Spree Window"
        description={`Are you sure you want to delete this shopping spree window?`}
        btnText="Delete"
      />

      {dataModalOpen && (
        <SpreeDataModal open={dataModalOpen} onClose={() => setDataModalOpen(false)} />
      )}
    </Box>
  )
}

export default ShoppingSpreeConfiguration
